import { useCallback, useEffect, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import { errorNormalizer } from "../../../shared/Helpers/functions";
import {
  ACTIONS,
  initialTreatmentPlaningState,
  treatmentPlaningReducer,
} from "./treatmentPlanningReducer";
import TreatmentPlanningData from "./treatmentPlanningData";
import { useResetCalendarData } from "../../Calendar/Helpers/hooks";
import SoapNoteButtons from "../components/soapNoteButtons";

import {
  getSoapNote,
  updateTreatmentPlanning,
} from "../../../redux/State/clientSlice/soapNoteSlice";
import { DispatchProperties, useSelector } from "../../../redux/store";
import {
  setDate,
  setEndTime,
  setStartTime,
} from "../../../redux/State/clientSlice/sessionSlice";
import {
  TreatmentPlanningProperties,
  UpdateTreatmentPlanningProperties,
} from "../../../redux/API/ClientAPIHelpers/soapNoteProperties";

const TreatmentPlanning = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetData } = useResetCalendarData();

  const reportId = params.get("reportId");
  const clientId = params.get("clientId");
  const sessionId = params.get("sessionId");
  const forcedOperation =
    params.get("forcedOperation") === "true" ? true : false;

  const sessionInfo = useSelector((state) => state.session.sessionInfo);
  const [data, setData] = useReducer(
    treatmentPlaningReducer,
    initialTreatmentPlaningState
  );

  const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (currentUpdateRequestRef.current) {
        console.log("Aborting request on unmount");
        currentUpdateRequestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!reportId || !clientId || !sessionId) return;

    dispatch(getSoapNote({ reportId, clientId }))
      .then(unwrapResult)
      .then((response) => {
        const {
          participantResponses,
          treatmentUpdateResponses,
          location,
          date,
          startTime,
          endTime,
          userTelehealthLocation,
          clientTelehealthLocation,
        } = response as TreatmentPlanningProperties;
        const participants = participantResponses?.map((x) => x.id);
        const treatmentUpdates = treatmentUpdateResponses?.map((x) => x.id);

        dispatch(setDate(dayjs(date).format()));
        dispatch(setStartTime(dayjs(startTime).format()));
        dispatch(setEndTime(dayjs(endTime).format()));

        setData({
          type: ACTIONS.setState,
          payload: response,
        });

        setData({
          type: ACTIONS.setTreatmentUpdateResponses,
          payload: treatmentUpdates,
        });

        setData({
          type: ACTIONS.setParticipants,
          payload: participants,
        });

        if (!!location) {
          setData({
            type: ACTIONS.setLocationId,
            payload: location.id,
          });
        }
        if (!!clientTelehealthLocation) {
          setData({
            type: ACTIONS.setClientTelehealthLocationId,
            payload: clientTelehealthLocation.id,
          });
        }
        if (!!userTelehealthLocation) {
          setData({
            type: ACTIONS.setUserTelehealthLocationId,
            payload: userTelehealthLocation.id,
          });
        }
      })
      .catch(errorNormalizer);
  }, [dispatch, clientId, reportId, sessionId]);

  const updateSoapNoteHandler = useCallback(
    (submit: boolean) => {
      if (currentUpdateRequestRef.current) {
        console.log("Aborting previous pending request");
        currentUpdateRequestRef.current.abort();
      }
      if (!reportId || !clientId || !data) return;
      const { date, startTime, endTime } = sessionInfo;
      const allData: UpdateTreatmentPlanningProperties = {
        ...data,
        date,
        startTime,
        endTime,
        reportId,
        submit,
        forcedOperation,
        clientId,
      };
      const requestPromise = dispatch(updateTreatmentPlanning(allData));
      currentUpdateRequestRef.current = requestPromise;

      requestPromise
        .then(unwrapResult)
        .then(() => {
          if (!submit || !isMounted.current) return;
          resetData();
          toast("Success");
          navigate("/calendar");
        })
        .catch(errorNormalizer)
        .finally(() => {
          if (currentUpdateRequestRef.current === requestPromise) {
            currentUpdateRequestRef.current = null;
          }
        })
    },
    [
      reportId,
      clientId,
      forcedOperation,
      data,
      sessionInfo,
      navigate,
      resetData,
      dispatch,
    ]
  );
  const saveHandler = useCallback(
    () => updateSoapNoteHandler(false),
    [updateSoapNoteHandler]
  );
  const submitHandler = useCallback(
    () => updateSoapNoteHandler(true),
    [updateSoapNoteHandler]
  );

  return (
    <>
      <TreatmentPlanningData setData={setData} data={data} save={saveHandler} />
      <SoapNoteButtons save={saveHandler} submit={submitHandler} />
    </>
  );
};

export default TreatmentPlanning;
